/**
 * The `source` vocabularies for calibres and their images.
 *
 * A module with NO Firebase import, deliberately. These lists are pure data and
 * the rule they encode (below) is worth testing directly, but importing
 * `services/firebase` from a test drags in `utils/imageConversion` → `heic2any`
 * (touches `window` at module scope) and boots App Check off `self` — so a test
 * of four string arrays ends up initialising Firebase. Same reasoning that keeps
 * `extractorJobs.ts` free of it.
 *
 * `services/firebase` re-exports everything here, so every existing import path
 * keeps working and callers need not know about the split.
 *
 * ── the rule ──────────────────────────────────────────────────────────────
 * `watchkeeper` — a photograph we own, rather than one supplied by the brand or
 * the movement maker — may be attributed to an IMAGE and never to a CALIBRE. A
 * movement is made by a brand or a manufacturer and never by us, and the
 * calibre-level `source` is read by the RN app as tier 3 of the image
 * attribution cascade, so a wrong value there propagates into how images are
 * credited.
 *
 * Four lists rather than two, because the manual pickers and the bulk tools
 * offer different breadth: the bulk tools additionally expose the values set
 * programmatically by import/upload flows (`manual`, `calibrecorner`,
 * `user_submitted`), which a human never picks from scratch.
 *
 *              │ calibre-level                      │ image
 *   ───────────┼────────────────────────────────────┼──────────────────────────
 *   manual     │ CALIBRE_LEVEL_ATTRIBUTION_SOURCES  │ CALIBRE_IMAGE_ATTRIBUTION_SOURCES
 *   bulk       │ CALIBRE_ENTITY_SOURCES             │ CALIBRE_IMAGE_SOURCES
 *
 * The two right-hand lists carry `watchkeeper`; the two left-hand ones must not.
 * Kept as separate lists rather than one list filtered at each call site, so the
 * exclusion is enforced by the type system instead of by whichever array a JSX
 * map happens to reference — it has been nearly broken twice, once through each
 * door.
 */

/** Per-IMAGE `source` values the bulk tool may set (drives RN captions). */
export const CALIBRE_IMAGE_SOURCES = [
  'manual',
  'brand',
  'manufacturer',
  'calibrecorner',
  'user_submitted',
  'watchkeeper',
] as const;
export type CalibreImageSource = (typeof CALIBRE_IMAGE_SOURCES)[number];

/**
 * ENTITY-level `source` values the bulk tool may set — the calibre document's
 * own `source`, not its images'. The image list minus `watchkeeper`.
 */
export const CALIBRE_ENTITY_SOURCES = [
  'manual',
  'brand',
  'manufacturer',
  'calibrecorner',
  'user_submitted',
] as const;
export type CalibreEntitySource = (typeof CALIBRE_ENTITY_SOURCES)[number];

/**
 * The attribution values an admin manually picks for a CALIBRE: `brand` (an
 * in-house movement, attributed to the watch brand) or `manufacturer` (a
 * third-party movement, attributed to the movement maker). The programmatic
 * values remain valid — they are simply not offered here.
 */
export const CALIBRE_LEVEL_ATTRIBUTION_SOURCES = ['brand', 'manufacturer'] as const;

/** The same pair plus `watchkeeper`, for one calibre IMAGE. */
export const CALIBRE_IMAGE_ATTRIBUTION_SOURCES = [
  'brand',
  'manufacturer',
  'watchkeeper',
] as const;

/**
 * @deprecated Prefer {@link CALIBRE_LEVEL_ATTRIBUTION_SOURCES} (or
 * {@link CALIBRE_IMAGE_ATTRIBUTION_SOURCES} for a per-image picker). Kept
 * pointing at the calibre-level list so any consumer that predates the split
 * keeps the values it already had.
 */
export const CALIBRE_ATTRIBUTION_SOURCES = CALIBRE_LEVEL_ATTRIBUTION_SOURCES;

/**
 * Calibre-level attribution. Deliberately NOT widened to the image list:
 * `resolveDefaultCalibreSource` answers "who made this movement", and
 * `watchkeeper` is never an answer to that.
 */
export type CalibreAttributionSource =
  (typeof CALIBRE_LEVEL_ATTRIBUTION_SOURCES)[number];
